/**
 * DASHBOARD NAV - Control Plane Sync V2
 * =====================================
 * 
 * Navigation horizontale pilotée par le Control Plane.
 * 
 * SOURCE DE VÉRITÉ: useRetailerPages() hook
 * - Visibilité: is_published_retailer (Control Plane)
 * - Ordre: display_order (Control Plane)
 * 
 * SAFE MODE: Ne modifie aucune route, lecture seule du Control Plane.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Package, 
  Star, 
  Tag, 
  TrendingUp, 
  Users, 
  LayoutDashboard,
  ShoppingCart,
  Heart,
  Calendar,
  Sparkles,
  Layers,
  User,
  Image,
  FileText,
  Percent,
  Building2,
  Handshake,
  ShieldCheck,
  BookOpen,
  Upload
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useRetailerPages } from '../hooks/useRetailerPages';

// Mapping des icônes par ID de page
const ICON_MAP = {
  'nouveautes': Sparkles,
  'precommandes': Calendar,
  'offres': Percent,
  'selection': Star,
  'bestsellers': TrendingUp,
  'tcg': Layers,
  'partenaires': Handshake,
  'blog': FileText
};

const getIconForPage = (pageId, iconName) => {
  // 1. Essayer l'icône configurée
  if (iconName) {
    const iconComponent = {
      'Sparkles': Sparkles,
      'Calendar': Calendar,
      'CalendarClock': Calendar,
      'Percent': Percent,
      'Star': Star,
      'TrendingUp': TrendingUp,
      'Layers': Layers,
      'Heart': Heart,
      'User': User,
      'FileText': FileText,
      'Handshake': Handshake,
      'Home': Home,
      'Package': Package
    }[iconName];
    if (iconComponent) return iconComponent;
  }
  
  // 2. Fallback par ID
  return ICON_MAP[pageId] || Package;
};

const DashboardNav = () => {
  const location = useLocation();
  const { user } = useAuth();
  
  // Control Plane: Source unique pour la navigation retailer
  const { menuPages, hubPages, cmsPages, loading, pages } = useRetailerPages();

  const isActive = (path) => location.pathname === path;

  // Navigation Partenaire (statique - pas de Control Plane)
  const partenaireLinks = [
    { path: '/dashboard/partner', label: 'Accueil', icon: Home },
    { path: '/dashboard/partner/produits', label: 'Mes Produits', icon: Package },
    { path: '/dashboard/partner/offres', label: 'Mes Offres', icon: Percent },
    { path: '/dashboard/profil', label: 'Mon Compte', icon: User },
  ];

  // Navigation Admin (statique - pas de Control Plane)
  const adminLinks = [
    { path: '/dashboard/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/dashboard/admin/produits', label: 'Produits', icon: Package },
    { path: '/dashboard/admin/partenaires', label: 'Partenaires', icon: Building2 },
    { path: '/dashboard/admin/organisations', label: 'Organisations', icon: Building2 },
    { path: '/dashboard/admin/offres', label: 'Offres', icon: Tag },
    { path: '/dashboard/admin/publications', label: 'Publications', icon: FileText },
    { path: '/dashboard/admin/pages', label: 'Pages', icon: Image },
    { path: '/dashboard/admin/global-blocks', label: 'Blocs', icon: Layers },
    { path: '/dashboard/admin/vitrines', label: 'Vitrines', icon: Star },
    { path: '/dashboard/admin/utilisateurs', label: 'Utilisateurs', icon: User },
    { path: '/dashboard/admin/referentiels', label: 'Référentiels', icon: BookOpen },
    { path: '/dashboard/admin/import-v2', label: 'Import V2', icon: Upload },
    { path: '/dashboard/admin/audit', label: 'Audit', icon: ShieldCheck },
    { path: '/dashboard/admin/invitations', label: 'Invitations', icon: BookOpen },
  ];

  // Navigation Retailer (dynamique via Control Plane)
  const retailerLinks = React.useMemo(() => {
    const links = [];
    
    // Ajouter les pages HUB métier (Control Plane: visibles et dans l'ordre)
    hubPages.forEach(page => {
      links.push({
        path: page.route,
        label: page.title,
        icon: getIconForPage(page.id, page.icon),
        id: page.id,
        displayOrder: page.display_order
      });
    });
    
    // Ajouter les pages CMS métier (Control Plane: visibles)
    cmsPages.forEach(page => {
      links.push({
        path: page.route,
        label: page.title,
        icon: getIconForPage(page.id, page.icon),
        id: page.id,
        displayOrder: page.display_order
      });
    });
    
    // Trier par display_order (Control Plane)
    return links.sort((a, b) => a.displayOrder - b.displayOrder);
  }, [hubPages, cmsPages]);

  // Don't render navigation until pages are loaded (évite flash)
  if (loading && user?.role === 'retailer') {
    return (
      <nav className="sticky top-[112px] bg-white border-b z-40" data-testid="dashboard-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-[38px]">
            <div className="animate-pulse h-4 w-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="bg-white/95 backdrop-blur-sm border-b z-40" style={{ borderColor: 'var(--ludo-border)' }} data-testid="dashboard-nav">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-[44px] gap-1">
            {/* Dynamic menu for retailer users - 100% Control Plane */}
            {user?.role === 'retailer' && retailerLinks.map((link, i) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-1.5 text-sm font-medium transition-all duration-200 rounded-full ${
                  isActive(link.path)
                    ? 'text-white'
                    : 'hover:bg-black/[0.04]'
                }`}
                style={isActive(link.path) 
                  ? { background: 'var(--ludo-text)', color: 'white' } 
                  : { color: 'var(--ludo-text-secondary)' }
                }
                data-testid={`nav-link-${link.id}`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Admin links (static) */}
            {user?.role === 'admin' && adminLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-1.5 text-xs font-medium transition-all duration-200 rounded-full whitespace-nowrap ${
                  isActive(link.path)
                    ? 'text-white'
                    : 'hover:bg-black/[0.04]'
                }`}
                style={isActive(link.path) 
                  ? { background: 'var(--ludo-text)', color: 'white' } 
                  : { color: 'var(--ludo-text-secondary)' }
                }
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Partner links (static) */}
            {user?.role === 'partenaire' && partenaireLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-1.5 text-sm font-medium transition-all duration-200 rounded-full ${
                  isActive(link.path)
                    ? 'text-white'
                    : 'hover:bg-black/[0.04]'
                }`}
                style={isActive(link.path) 
                  ? { background: 'var(--ludo-text)', color: 'white' } 
                  : { color: 'var(--ludo-text-secondary)' }
                }
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
        </div>
      </div>
    </nav>
  );
};

export default DashboardNav;
